import React, { useState, useEffect } from 'react';
import { useAuth } from '../data/auth';

/* ═══════════════════════════════════════════
   SIGNUP PAGE — 4-step flow
   Step 1: Name, email
   Step 2: Plan selection + seat picker → Stripe checkout
   Step 3: Stripe return — verifying payment
   Step 4: Set password → account created (password is NEVER stored in localStorage)
   ═══════════════════════════════════════════ */

const PLANS = [
  { id: "annual", name: "Annual", price: 159, period: "per seat/month", billing: "Billed annually", deposit: 0.25, depositLabel: "25% due today", badge: "Best Value", savings: "Save $20/seat per month" },
  { id: "biannual", name: "Bi-Annual", price: 169, period: "per seat/month", billing: "Billed every 6 months", deposit: 0.25, depositLabel: "25% due today", badge: "Most Popular", savings: "Save $10/seat per month" },
  { id: "monthly", name: "Monthly", price: 179, period: "per seat/month", billing: "Billed monthly", deposit: 1.0, depositLabel: "Full amount today", badge: null, savings: null },
];

export default function SignupPage() {
  const auth = useAuth();
  const [step, setStep] = useState(1); // 1=info, 2=plan, 3=verifying, 4=set-password
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Plan state
  const [selectedPlan, setSelectedPlan] = useState("annual");
  const [seats, setSeats] = useState(1);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Post-verify password step state
  const [postPayEmail, setPostPayEmail] = useState("");
  const [postPayFullName, setPostPayFullName] = useState("");
  const [postPayPassword, setPostPayPassword] = useState("");
  const [postPayPasswordConfirm, setPostPayPasswordConfirm] = useState("");
  const [postPaySubmitting, setPostPaySubmitting] = useState(false);

  // Check for Stripe return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (sessionId) {
      setStep(3);
      verifyPaymentAndSignup(sessionId);
    }
  }, []);

  const verifyPaymentAndSignup = async (sessionId) => {
    setLoading(true);
    setError("");
    try {
      const resp = await fetch("/api/stripe/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId }),
      });
      const data = await resp.json();
      if (data.success && data.verified) {
        const signupData = JSON.parse(localStorage.getItem("coreflow_signup") || "{}");
        if (signupData.email) {
          // Persist non-sensitive plan metadata for the eventual auth callback.
          localStorage.setItem("coreflow_plan", JSON.stringify({
            plan: data.plan || signupData.plan,
            seats: data.seats || signupData.seats,
            stripeCustomerId: data.customer_id,
            stripeSubscriptionId: data.subscription_id,
            stripeSessionId: sessionId,
          }));
          // Password is NOT stored in localStorage anymore — user sets it now.
          setPostPayEmail(signupData.email);
          setPostPayFullName(signupData.fullName || "");
          setStep(4);
        } else {
          setError("Payment verified but signup data was lost. Please try signing up again.");
          setStep(1);
        }
      } else {
        setError(data.error || "Payment verification failed. Please try again or contact support.");
        setStep(1);
      }
    } catch (e) {
      setError("Verification error: " + e.message);
      setStep(1);
    }
    setLoading(false);
    window.history.pushState({}, "", "/signup");
  };

  const handlePostPaySubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!postPayPassword || postPayPassword.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (postPayPassword !== postPayPasswordConfirm) {
      setError("Passwords do not match");
      return;
    }
    setPostPaySubmitting(true);
    try {
      const result = await auth.signup(postPayEmail, postPayPassword, postPayFullName);
      if (result.success) {
        // Clear the non-sensitive signup scratch state now that the account exists.
        localStorage.removeItem("coreflow_signup");
        if (result.needsConfirmation) {
          setEmail(postPayEmail);
          setShowConfirmation(true);
        }
        // If no confirmation needed, auth state will update and App.js will redirect.
      } else {
        setError(result.error || "Account creation failed. Contact support.");
      }
    } catch (err) {
      setError("Account creation error: " + err.message);
    }
    setPostPaySubmitting(false);
  };

  const handleStep1 = (e) => {
    e.preventDefault();
    if (!fullName || !email) { setError("Please fill in all fields"); return; }
    setError("");
    setStep(2);
  };

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    setError("");
    // NOTE: password intentionally omitted — never stored in localStorage.
    // User sets their password on the post-payment return step (step 4).
    localStorage.setItem("coreflow_signup", JSON.stringify({ email, fullName, plan: selectedPlan, seats }));
    try {
      const resp = await fetch("/api/stripe/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan: selectedPlan,
          seats,
          email,
          name: fullName,
          return_url: window.location.origin + "/signup?session_id={CHECKOUT_SESSION_ID}",
        }),
      });
      const data = await resp.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || "Failed to create checkout session");
      }
    } catch (e) {
      setError("Checkout error: " + e.message);
    }
    setCheckoutLoading(false);
  };

  const goLogin = () => {
    window.history.pushState({}, "", "/login");
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  const plan = PLANS.find(p => p.id === selectedPlan);
  const totalMonthly = plan.price * seats;
  const billingPeriod = selectedPlan === "annual" ? 12 : selectedPlan === "biannual" ? 6 : 1;
  const totalBilling = totalMonthly * billingPeriod;
  const dueToday = Math.round(totalBilling * plan.deposit);

  // Styles
  const cardStyle = {
    width: 420, background: "#2C2E33", borderRadius: 12, border: "1px solid rgba(217,219,221,0.10)",
    padding: "32px 36px", boxShadow: "0 24px 80px rgba(0,0,0,0.5)",
  };
  const inputStyle = {
    width: "100%", padding: "12px 14px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)",
    background: "rgba(255,255,255,0.05)", color: "#FAFAFA", fontSize: 13, outline: "none",
    fontFamily: "inherit", boxSizing: "border-box", transition: "border-color 0.15s",
  };
  const btnPrimary = {
    width: "100%", padding: "12px 0", borderRadius: 8, border: "none",
    background: "#4059AD", color: "#FAFAFA", fontSize: 13, fontWeight: 400,
    cursor: "pointer", fontFamily: "inherit", transition: "opacity 0.15s",
  };
  const btnOutline = {
    width: "100%", padding: "12px 0", borderRadius: 8,
    border: "1px solid rgba(217,219,221,0.12)", background: "transparent",
    color: "#FAFAFA", fontSize: 13, fontWeight: 400, cursor: "pointer", fontFamily: "inherit",
  };
  const linkStyle = { color: "#4059AD", background: "none", border: "none", cursor: "pointer", fontSize: 12, fontFamily: "inherit", padding: 0 };

  // Email confirmation screen
  if (showConfirmation) {
    return (
      <div style={{ minHeight: "100vh", background: "#1E1F23", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ ...cardStyle, textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>✉️</div>
          <div style={{ fontSize: 18, fontWeight: 400, color: "#FAFAFA", marginBottom: 8 }}>Check your email</div>
          <div style={{ fontSize: 13, color: "rgba(250,250,250,0.5)", lineHeight: 1.6 }}>
            We sent a confirmation link to <span style={{ color: "#FAFAFA" }}>{email}</span>. Click the link to activate your account.
          </div>
          <div style={{ fontSize: 12, color: "rgba(250,250,250,0.35)", marginTop: 12, lineHeight: 1.6 }}>
            Your payment has been processed. Your account will be ready as soon as you confirm your email.
          </div>
          <button onClick={goLogin} style={{ ...linkStyle, marginTop: 20, fontSize: 13 }}>Go to login</button>
        </div>
      </div>
    );
  }

  // Verifying payment screen
  if (step === 3 && loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#1E1F23", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ ...cardStyle, textAlign: "center" }}>
          <div style={{ fontSize: 28, marginBottom: 16 }}>💳</div>
          <div style={{ fontSize: 16, fontWeight: 400, color: "#FAFAFA", marginBottom: 8 }}>Verifying your payment...</div>
          <div style={{ fontSize: 13, color: "rgba(250,250,250,0.4)" }}>Setting up your account. This only takes a moment.</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#1E1F23", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 24, overflowY: "auto", padding: "40px 20px" }}>
      <style>{`
        input::placeholder { color: rgba(250,250,250,0.25); }
        input:focus { border-color: rgba(64,89,173,0.5) !important; }
      `}</style>

      {/* Logo */}
      <img src="/coreflow-logo-white.png" alt="CoreFlow" style={{ height: 22, objectFit: "contain", marginBottom: 8 }} />

      {/* ═══ STEP 1: Account Info ═══ */}
      {step === 1 && (
        <div style={cardStyle}>
          <div style={{ fontSize: 18, fontWeight: 400, color: "#FAFAFA", marginBottom: 4 }}>Create your account</div>
          <div style={{ fontSize: 12, color: "rgba(250,250,250,0.4)", marginBottom: 24 }}>Start your CoreFlow in minutes</div>

          {/* Step indicator */}
          <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
            <div style={{ flex: 1, height: 3, borderRadius: 2, background: "#4059AD" }} />
            <div style={{ flex: 1, height: 3, borderRadius: 2, background: "rgba(217,219,221,0.12)" }} />
          </div>

          <form onSubmit={handleStep1}>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={{ fontSize: 11, color: "rgba(250,250,250,0.4)", display: "block", marginBottom: 6 }}>Full Name</label>
                <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="John Smith" style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: "rgba(250,250,250,0.4)", display: "block", marginBottom: 6 }}>Work Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" style={inputStyle} />
              </div>
              <div style={{ fontSize: 11, color: "rgba(250,250,250,0.35)", lineHeight: 1.5 }}>
                You'll set your password after payment is confirmed.
              </div>
              {error && <div style={{ fontSize: 12, color: "#E53935", padding: "8px 12px", background: "rgba(229,57,53,0.08)", borderRadius: 8 }}>{error}</div>}
              <button type="submit" style={btnPrimary}>Continue to Plan Selection</button>
            </div>
          </form>

          <div style={{ fontSize: 11, color: "rgba(250,250,250,0.35)", textAlign: "center", marginTop: 14, lineHeight: 1.5 }}>
            Already have an account? <button onClick={goLogin} style={{ ...linkStyle, fontSize: 11 }}>Sign in</button>
          </div>
        </div>
      )}

      {/* ═══ STEP 2: Plan Selection ═══ */}
      {step === 2 && (
        <div style={{ width: 680, maxWidth: "95vw" }}>
          {/* Step indicator */}
          <div style={{ display: "flex", gap: 8, marginBottom: 24, maxWidth: 420, margin: "0 auto 24px" }}>
            <div style={{ flex: 1, height: 3, borderRadius: 2, background: "#4059AD" }} />
            <div style={{ flex: 1, height: 3, borderRadius: 2, background: "#4059AD" }} />
          </div>

          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <div style={{ fontSize: 22, fontWeight: 400, color: "#FAFAFA", marginBottom: 6 }}>Choose your plan</div>
            <div style={{ fontSize: 13, color: "rgba(250,250,250,0.4)" }}>All plans include every feature. Pay per seat.</div>
          </div>

          {/* Plan cards */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 24 }}>
            {PLANS.map(p => {
              const active = selectedPlan === p.id;
              return (
                <button key={p.id} onClick={() => setSelectedPlan(p.id)} style={{
                  background: active ? "rgba(64,89,173,0.08)" : "#2C2E33",
                  border: active ? "2px solid #4059AD" : "2px solid rgba(217,219,221,0.08)",
                  borderRadius: 12, padding: "24px 20px", cursor: "pointer", textAlign: "left",
                  fontFamily: "inherit", position: "relative", transition: "all 0.15s",
                }}>
                  {p.badge && <div style={{ position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)", padding: "3px 12px", borderRadius: 8, background: "#4059AD", color: "#FAFAFA", fontSize: 10, fontWeight: 400, whiteSpace: "nowrap" }}>{p.badge}</div>}
                  <div style={{ fontSize: 14, fontWeight: 400, color: "#FAFAFA", marginBottom: 12 }}>{p.name}</div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginBottom: 4 }}>
                    <span style={{ fontSize: 32, fontWeight: 400, color: "#FAFAFA", lineHeight: 1 }}>${p.price}</span>
                    <span style={{ fontSize: 12, color: "rgba(250,250,250,0.35)" }}>/seat/mo</span>
                  </div>
                  <div style={{ fontSize: 11, color: "rgba(250,250,250,0.35)", marginBottom: 8 }}>{p.billing}</div>
                  {p.savings && <div style={{ fontSize: 11, color: "#2E8B57", fontWeight: 400 }}>{p.savings}</div>}
                  <div style={{ fontSize: 10, color: "rgba(250,250,250,0.3)", marginTop: 8, paddingTop: 8, borderTop: "1px solid rgba(217,219,221,0.06)" }}>{p.depositLabel}</div>
                </button>
              );
            })}
          </div>

          {/* Seat selector + summary */}
          <div style={{ background: "#2C2E33", borderRadius: 12, border: "1px solid rgba(217,219,221,0.10)", padding: "24px 28px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 400, color: "#FAFAFA" }}>Number of seats</div>
                <div style={{ fontSize: 11, color: "rgba(250,250,250,0.35)", marginTop: 2 }}>Each team member needs one seat</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <button onClick={() => setSeats(Math.max(1, seats - 1))} style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>-</button>
                <span style={{ fontSize: 20, fontWeight: 400, color: "#FAFAFA", minWidth: 32, textAlign: "center" }}>{seats}</span>
                <button onClick={() => setSeats(Math.min(50, seats + 1))} style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>+</button>
              </div>
            </div>

            {/* Price breakdown */}
            <div style={{ borderTop: "1px solid rgba(217,219,221,0.08)", paddingTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "rgba(250,250,250,0.5)" }}>
                <span>{plan.name} plan: ${plan.price}/seat x {seats} seat{seats > 1 ? "s" : ""}</span>
                <span>${totalMonthly.toLocaleString()}/mo</span>
              </div>
              {selectedPlan !== "monthly" && (
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "rgba(250,250,250,0.5)" }}>
                  <span>{billingPeriod} month billing period</span>
                  <span>${totalBilling.toLocaleString()}</span>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, fontWeight: 400, color: "#FAFAFA", paddingTop: 8, borderTop: "1px solid rgba(217,219,221,0.08)" }}>
                <span>Due today{selectedPlan !== "monthly" ? " (25% deposit)" : ""}</span>
                <span>${dueToday.toLocaleString()}</span>
              </div>
            </div>

            {error && <div style={{ fontSize: 12, color: "#E53935", padding: "8px 12px", background: "rgba(229,57,53,0.08)", borderRadius: 8, marginTop: 12 }}>{error}</div>}

            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button onClick={() => { setStep(1); setError(""); }} style={{ ...btnOutline, flex: "0 0 auto", width: "auto", padding: "12px 24px" }}>Back</button>
              <button onClick={handleCheckout} disabled={checkoutLoading} style={{ ...btnPrimary, flex: 1, opacity: checkoutLoading ? 0.6 : 1 }}>
                {checkoutLoading ? "Redirecting to payment..." : `Continue to Payment — $${dueToday.toLocaleString()}`}
              </button>
            </div>
          </div>

          <div style={{ textAlign: "center", marginTop: 16, fontSize: 11, color: "rgba(250,250,250,0.25)" }}>
            Secure payment powered by Stripe. Cancel anytime.
          </div>
        </div>
      )}

      {/* ═══ STEP 4: Set Password (post-payment) ═══ */}
      {step === 4 && !showConfirmation && (
        <div style={cardStyle}>
          <div style={{ fontSize: 28, marginBottom: 12, textAlign: "center" }}>✅</div>
          <div style={{ fontSize: 18, fontWeight: 400, color: "#FAFAFA", marginBottom: 4, textAlign: "center" }}>Payment verified</div>
          <div style={{ fontSize: 12, color: "rgba(250,250,250,0.4)", marginBottom: 20, textAlign: "center" }}>
            Set a password to finish creating your account for <span style={{ color: "#FAFAFA" }}>{postPayEmail}</span>.
          </div>
          <form onSubmit={handlePostPaySubmit}>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={{ fontSize: 11, color: "rgba(250,250,250,0.4)", display: "block", marginBottom: 6 }}>Password</label>
                <input type="password" value={postPayPassword} onChange={e => setPostPayPassword(e.target.value)} placeholder="Min 6 characters" style={inputStyle} autoFocus />
              </div>
              <div>
                <label style={{ fontSize: 11, color: "rgba(250,250,250,0.4)", display: "block", marginBottom: 6 }}>Confirm Password</label>
                <input type="password" value={postPayPasswordConfirm} onChange={e => setPostPayPasswordConfirm(e.target.value)} placeholder="Re-enter password" style={inputStyle} />
              </div>
              {error && <div style={{ fontSize: 12, color: "#E53935", padding: "8px 12px", background: "rgba(229,57,53,0.08)", borderRadius: 8 }}>{error}</div>}
              <button type="submit" disabled={postPaySubmitting} style={{ ...btnPrimary, opacity: postPaySubmitting ? 0.6 : 1 }}>
                {postPaySubmitting ? "Creating account..." : "Create Account"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Footer */}
      <div style={{ fontSize: 11, color: "rgba(250,250,250,0.2)", marginTop: 8 }}>
        © {new Date().getFullYear()} CoreFlow. All rights reserved.
      </div>
    </div>
  );
}
